export function toUrl(params: any): string {
  return `?${new URLSearchParams(params).toString()}`;
}
export function roundNumber(number: number): number {
  return Math.round((number + Number.EPSILON) * 100) / 100;
}

export function debounce(
  action: () => any,
  debounceChecker: number | null | undefined,
  timeout: number = 300,
): number {
  if (debounceChecker) {
    clearTimeout(debounceChecker);
  }
  return setTimeout(action, timeout);
}

export function normalizeKeys(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(normalizeKeys);
  } else if (obj && typeof obj === "object") {
    return Object.keys(obj).reduce((acc: any, key: string) => {
      const newKey = key.charAt(0).toLowerCase() + key.slice(1);
      acc[newKey] = normalizeKeys(obj[key]);
      return acc;
    }, {});
  }
  return obj;
}

/**
 * Strips non-numeric characters from a string, normalizes comma to dot,
 * and parses the result as a float.
 *
 * Allowed characters: digits (0-9), dot (.), comma (,), minus (-).
 * Everything else is removed.
 *
 * @param value - The raw string from an input field
 * @returns The parsed number, or 0 if the result is not a valid number
 */
export function parseNumericValue(value: string): number {
  const filtered = value.replace(/[^0-9.,\-]/g, "");
  const normalized = filtered.replace(",", ".");
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? 0 : parsed;
}

export function normalizeNumericInput(value: string): string {
  const filtered = value.replace(/[^0-9.,\-]/g, "");
  const normalized = filtered.replace(",", ".");
  return normalized;
}

/**
 * Handles an input Event from a raw <input> element for numeric fields.
 *
 * Does the same filtering/parsing as parseNumericValue, but also updates the
 * DOM element's displayed value to strip out invalid characters in real time
 * (so the user sees "12.5" not "12.5abc").
 *
 * Use this for native <input> elements. For <b-form-input> (BootstrapVue),
 * use parseNumericValue directly since BootstrapVue passes the value string,
 * not the Event.
 *
 * @param event - The native input Event
 * @returns The parsed number
 */
export function filterNumericInputEvent(event: Event): number {
  const input = event.target as HTMLInputElement;
  const filtered = input.value.replace(/[^0-9.,\-]/g, "");
  if (filtered !== input.value) input.value = filtered;
  const normalized = filtered.replace(",", ".");
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? 0 : parsed;
}

export function normalizeCoefficientValuesOnly(obj: any): any {
  if (!obj || typeof obj !== "object") return obj;

  return Object.keys(obj).reduce((acc: any, key: string) => {
    const original = obj[key];

    if (original && typeof original === "object" && !Array.isArray(original)) {
      // Only normalize keys inside each coefficient object
      const normalizedInner = Object.keys(original).reduce(
        (innerAcc: any, innerKey: string) => {
          const lowerKey = innerKey.charAt(0).toLowerCase() + innerKey.slice(1);
          innerAcc[lowerKey] = original[innerKey];
          return innerAcc;
        },
        {},
      );
      acc[key] = normalizedInner;
    } else {
      acc[key] = original;
    }

    return acc;
  }, {});
}

export function diffDays(date1: Date, date2: Date): number {
  date1.setHours(0, 0, 0, 0);
  date2.setHours(0, 0, 0, 0);

  return Math.round(
    (date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24),
  );
}
